import { useCallback, useMemo } from 'react';

/**
 * authCode를 sessionStorage에 저장/복구하는 hook.
 *
 * 미로그인/채널생성 흐름에서 페이지 리로드/리다이렉트가 발생해도
 * authCode를 잃지 않도록 보관한다.
 *
 * - key: `schoolConnect:authCode`, `schoolConnect:expiresAt`
 * - 만료 timestamp 초과 시 자동으로 expired 처리되어 `null`을 반환한다.
 *
 * SSR 환경 안전성: 모든 sessionStorage 접근은 try/catch로 감싸 실패 시
 * 조용히 무시한다. (`window`/`sessionStorage`가 없는 환경에서도 throw하지 않음)
 */
export const AUTH_CODE_STORAGE_KEY = 'schoolConnect:authCode';
export const EXPIRES_AT_STORAGE_KEY = 'schoolConnect:expiresAt';

export interface StoredAuthCode {
  authCode: string;
  expiresAt: number; // unix ms timestamp
}

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStoredAuthCode(now: number = Date.now()): StoredAuthCode | null {
  const storage = getStorage();
  if (!storage) return null;

  try {
    const authCode = storage.getItem(AUTH_CODE_STORAGE_KEY);
    const expiresAtRaw = storage.getItem(EXPIRES_AT_STORAGE_KEY);

    if (!authCode || !expiresAtRaw) return null;

    const expiresAt = Number(expiresAtRaw);
    if (!Number.isFinite(expiresAt)) {
      clearStoredAuthCode();
      return null;
    }

    if (expiresAt <= now) {
      // 만료된 상태: 보관소도 정리
      clearStoredAuthCode();
      return null;
    }

    return { authCode, expiresAt };
  } catch {
    return null;
  }
}

export function writeStoredAuthCode(authCode: string, expiresAt: number): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.setItem(AUTH_CODE_STORAGE_KEY, authCode);
    storage.setItem(EXPIRES_AT_STORAGE_KEY, String(expiresAt));
  } catch {
    // sessionStorage가 quota 초과 등 실패: 무시 (저장 안 됨)
  }
}

export function clearStoredAuthCode(): void {
  const storage = getStorage();
  if (!storage) return;

  try {
    storage.removeItem(AUTH_CODE_STORAGE_KEY);
    storage.removeItem(EXPIRES_AT_STORAGE_KEY);
  } catch {
    // ignore
  }
}

interface UseConnectAuthCodeReturn {
  /** 보관된 authCode + 만료 timestamp. 만료/없으면 null. */
  stored: StoredAuthCode | null;
  /** authCode를 sessionStorage에 보관한다. */
  save: (authCode: string, expiresAtMs: number) => void;
  /** 보관소를 비운다. */
  clear: () => void;
}

/**
 * sessionStorage 기반 authCode 보관 hook.
 *
 * 컴포넌트 진입 시점에 1회 동기적으로 보관 상태를 읽는다.
 * `revalidate()` 이후의 보관 변경은 자동으로 반영하지 않는다 — 필요하다면
 * 호출자가 직접 page reload 또는 `useEffect`를 통해 다시 마운트한다.
 *
 * (sessionStorage는 동일 origin/탭에서만 동기화되므로 SWR/이벤트 기반 동기화는 과한 비용.
 *  실 사용 시나리오에서는 페이지 진입 시 1회 읽기로 충분하다.)
 */
export function useConnectAuthCode(): UseConnectAuthCodeReturn {
  // 마운트 시점에 한번만 읽는다 (lazy)
  const stored = useMemo(() => readStoredAuthCode(), []);

  const save = useCallback((authCode: string, expiresAtMs: number) => {
    writeStoredAuthCode(authCode, expiresAtMs);
  }, []);

  const clear = useCallback(() => {
    clearStoredAuthCode();
  }, []);

  return { stored, save, clear };
}
